import React from "react";
import Rectangle1 from "../assets/sainsburys.svg";
import Rectangle2 from "../assets/tesco.svg";
import Rectangle3 from "../assets/asda.svg";
import Rectangle4 from "../assets/morrisons.svg";

const retailerBannerByPlatform = {
  sainsbury: Rectangle1,
  tesco: Rectangle2,
  asda: Rectangle3,
  morrison: Rectangle4,
};

const retailerVisitUrls = {
  asda: "https://www.asda.com/",
  tesco: "https://www.tesco.com/",
  morrison: "https://groceries.morrisons.com/",
  sainsbury: "https://www.sainsburys.co.uk/",
};

export default function BasketSummarySection({ basket }) {
  if (!basket?.rows?.length) return null;

  return (
    <>
      <div className="rounded-2xl border border-[#2FC98D80] bg-[#EAFBF3] p-4 shadow-sm sm:p-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <span className="shrink-0 text-[24px] sm:text-[28px]" aria-hidden>
            🏆
          </span>
          <div className="min-w-0">
            <p className="text-[15px] font-bold leading-snug text-[#24303A] sm:text-[18px]">
              {basket.cheapest
                ? `Cheapest supermarket: ${basket.cheapest.label}`
                : "Multiple stores share the lowest basket total"}
            </p>
            {basket.savings != null && basket.expensiveStore && (
              <p className="mt-1 text-[13px] font-medium leading-snug text-[#1FA971] sm:text-[16px]">
                You&apos;ll save £{basket.savings.toFixed(2)} vs most expensive basket. Compared to{" "}
                {basket.expensiveStore.label}.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="my-4 inline-flex items-center justify-center gap-2 text-[18px] font-semibold text-[rgb(176,48,28)]">
        <span
          className="relative group inline-flex shrink-0"
          aria-label="Basket totals use standard prices"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 10v6" strokeLinecap="round" />
            <circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" />
          </svg>
        </span>
        <span>Basket totals currently use standard prices. Loyalty basket calculations are coming soon.</span>
      </div>

      <h2 className="text-[18px] font-semibold text-[#24303A] sm:text-[20px]">Basket total by store</h2>

      <div className="mt-2 space-y-3">
        {basket.rows.map((row) => {
          const bannerSrc = retailerBannerByPlatform[row.platform];
          const visitUrl = retailerVisitUrls[row.platform];
          const isComplete = row.complete !== false && Number.isFinite(row.total) && row.total > 0;
          const hasPrice = isComplete;
          const priceLabel =
            row.totalLabel ||
            (hasPrice ? `£${row.total.toFixed(2)}` : "Incomplete");
          const secondaryLabel =
            row.isBest
              ? null
              : row.deltaLabel ||
                (Number.isFinite(row.delta) ? `+£${row.delta.toFixed(2)}` : row.vsBest || row.coverage);

          return (
            <div
              key={row.platform}
              className={[
                "relative flex flex-col gap-3 rounded-2xl border px-3 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4",
                row.isBest ? "border-[#2FC98D80] bg-[#EAFBF3]" : "border-[#E6EAF0] bg-white",
              ].join(" ")}
            >
              {row.isBest && (
                <span className="absolute left-0 top-0">
                  <span className="relative inline-flex h-7 w-7 items-center justify-center">
                    <span
                      className="absolute inset-0 rounded-br-[10px] rounded-tr-none rounded-l-none bg-[#5B3CC4]"
                      aria-hidden
                    />
                    <svg
                      viewBox="0 0 24 24"
                      className="relative z-10 h-[24px] w-[24px]"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M12 3.2l2.55 5.17 5.7.83-4.12 4.02.97 5.68L12 16.48 6.9 18.92l.97-5.68L3.75 9.2l5.7-.83L12 3.2z"
                        fill="#FFFFFF"
                      />
                    </svg>
                  </span>
                </span>
              )}

              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md sm:h-11 sm:w-28">
                  {bannerSrc ? (
                    <img
                      src={bannerSrc}
                      alt={row.label}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-[11px] font-semibold text-[#24303A] sm:text-[12px]">
                      {row.label}
                    </span>
                  )}
                </div>

                <span
                  className={[
                    "text-[16px] font-bold sm:text-[18px]",
                    hasPrice ? "text-[#24303A]" : "text-[#6E7B86]",
                  ].join(" ")}
                >
                  {priceLabel}
                </span>
              </div>

              <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:shrink-0 sm:justify-end sm:gap-3">
                {row.isBest ? (
                  <span className="rounded-full bg-[#1FA971] px-2.5 py-1 text-[12px] font-semibold uppercase tracking-wide text-white sm:px-3 sm:text-[14px]">
                    Best Price
                  </span>
                ) : secondaryLabel ? (
                  <span className="rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-[#6E7B86] ring-1 ring-[#E6EAF0] sm:px-3 sm:text-[14px]">
                    {secondaryLabel}
                  </span>
                ) : null}

                <button
                  type="button"
                  className={[
                    "rounded-full px-3 py-1.5 text-[13px] font-semibold disabled:cursor-not-allowed sm:py-1 sm:text-[16px]",
                    visitUrl && hasPrice
                      ? "bg-[#7352C71A] text-[#7352C7]"
                      : "bg-[#7352C71A] text-slate-400",
                  ].join(" ")}
                  disabled={!visitUrl || !hasPrice}
                  onClick={() => {
                    if (!visitUrl || !hasPrice) return;
                    window.open(visitUrl, "_blank", "noopener,noreferrer");
                  }}
                >
                  Visit Store
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
